/*
 * Pure helpers of the OTA screen: file sizes, status badges, and the
 * "which gateway needs an update" computation.
 */
import { DeviceUpdateStatus, DeviceVersionItem, FirmwareItem } from '../../../types/otaUpdate';

export const formatFileSize = (bytes: number | null | undefined): string => {
  if (bytes == null) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export const statusBadgeClass = (status: string): string => {
  switch (status) {
    case 'completed': return 'status-badge completed';
    case 'in_progress': return 'status-badge in-progress';
    case 'pending': return 'status-badge pending';
    case 'failed': return 'status-badge failed';
    default: return 'status-badge';
  }
};

/** The most recent active firmware for a model and component type, if any. */
export const latestFirmwareFor = (firmware: FirmwareItem[], model: string, type: string): FirmwareItem | undefined =>
  firmware
    .filter((fw) => fw.is_active && fw.model === model && fw.type.toLowerCase() === type.toLowerCase())
    .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];

/**
 * One status row per gateway. A gateway reports one version per component;
 * the MAIN component (or the first one) stands for the gateway, and it needs
 * an update when the latest firmware for its model differs from what it runs.
 */
export const buildDeviceStatus = (versions: DeviceVersionItem[], firmware: FirmwareItem[]): DeviceUpdateStatus[] => {
  const byDevice = new Map<string, DeviceVersionItem[]>();
  for (const item of versions) {
    byDevice.set(item.device_id, [...(byDevice.get(item.device_id) || []), item]);
  }
  const rows: DeviceUpdateStatus[] = [];
  byDevice.forEach((components, deviceId) => {
    const main = components.find((v) => v.type === 'MAIN') || components[0];
    if (!main) return;
    const latest = latestFirmwareFor(firmware, main.model, main.type);
    rows.push({
      device_id: deviceId,
      device_name: main.serial || deviceId,
      device_type: main.type.toLowerCase(),
      current_version: main.version,
      latest_version: latest?.version || main.version,
      needs_update: latest ? latest.version !== main.version : false,
      last_updated: main.last_check,
    });
  });
  return rows;
};
